import React, {
   useCallback,
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
   fetchInstructors,
   removeInstructorAccount,
   updateInstructor,
} from "../../../store/instructorsSlice";
import { fetchCars, addCar, updateCar, removeCar } from "../../../store/carsSlice";
import { fetchUsers } from "../../../store/usersSlice";

import { updateUser as apiUpdateUser } from "../../../api/usersService";
import {
   DEFAULT_INSTRUCTOR_TYPE,
   getStaffTitle,
   instructorTypeHasCar,
   normalizeInstructorExam,
   normalizeInstructorType,
} from "../../../constants/instructors";
import { getAdminRatingsAll } from "../../../api/instructorRatingsService";
import { UserContext } from "../../../UserContext";
import { closePopup as closePopupUI } from "../../Common/popupUIStore";
import { getRatingCardStyle, getRatingCardStyleSoft } from "../../Common/ratingPalette";

import InstructorMainTab from "./InstructorMainTab";
import InstructorLessonsTab from "./InstructorLessonsTab";
import InstructorStatsTab from "./InstructorStatsTab";
import StaffProfileHead from "../StaffProfile/StaffProfileHead";
import { ownAvatarUrl, rememberAvatarChange } from "../../../utils/userAvatar";
import ProfileTabs from "../../Common/ProfileTabs";
import {
   TABS,
   DEFAULT_COLOR_INDEX,
   getInitials,
   getAvatarColorFromName,
   normalizeColorToCss,
   colorIndexFromColor,
   colorIndexToCss,
   normPlate,
   toApiGearbox,
   getCurrentMonthSlot,
   shiftMonthSlot,
   compareMonthSlots,
   buildVisibleMonthWindow,
   filterRatingsByPeriod,
   computeRatingStats,
   buildTrendBuckets,
   getRatingTimestamp,
   formatStatsPeriodLabel,
} from "./instructorProfileShared";

export default function InstructorProfileUI({ instructor }) {
   const dispatch = useDispatch();
   const { user: viewer } = useContext(UserContext) || {};
   const instructorId = String(instructor?.id ?? "").trim();

   const { list: instructors = [], status: instructorsStatus = "idle" } =
      useSelector((state) => state.instructors || {});
   const { list: cars = [], status: carsStatus = "idle" } = useSelector(
      (state) => state.cars || {},
   );
   const { list: users = [], status: usersStatus = "idle" } = useSelector(
      (state) => state.users || {},
   );

   const [tab, setTab] = useState("profile");
   const [saving, setSaving] = useState(false);
   const [deleting, setDeleting] = useState(false);
   const [saveError, setSaveError] = useState("");
   const [profileDirty, setProfileDirty] = useState(false);
   const [formData, setFormData] = useState({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      sector: "",
      instructorType: DEFAULT_INSTRUCTOR_TYPE,
      isExamen: false,
      instructorIdField: "",
      color: "",
      carPlate: "",
      gearbox: "manual",
      newPassword: "",
   });

   const [ratingsAnchor, setRatingsAnchor] = useState(() => getCurrentMonthSlot());
   const [ratingsFullYear, setRatingsFullYear] = useState(false);
   const [ratingsLoading, setRatingsLoading] = useState(false);
   const [ratingsError, setRatingsError] = useState("");
   const [ratingsAll, setRatingsAll] = useState([]);
   const ratingsCacheRef = useRef({ instructorId: "", rows: [] });

   useEffect(() => {
      if (instructorsStatus === "idle") dispatch(fetchInstructors());
      if (carsStatus === "idle") dispatch(fetchCars());
      if (usersStatus === "idle") dispatch(fetchUsers());
   }, [carsStatus, dispatch, instructorsStatus, usersStatus]);

   const carsByInstructorId = useMemo(() => {
      const map = new Map();

      for (const car of cars || []) {
         const key = String(car?.instructorId ?? "").trim();
         if (!key) continue;
         map.set(key, car);
      }

      return map;
   }, [cars]);

   const carsByPlate = useMemo(() => {
      const map = new Map();

      for (const car of cars || []) {
         const plate = normPlate(car?.plateNumber);
         if (!plate) continue;
         map.set(plate, car);
      }

      return map;
   }, [cars]);

   const currentInstructor = useMemo(() => {
      if (!instructorId) return instructor || null;

      return (
         (instructors || []).find(
            (row) => String(row?.id ?? "") === instructorId,
         ) ||
         instructor ||
         null
      );
   }, [instructor, instructorId, instructors]);

   const currentCar = useMemo(() => {
      if (!instructorId) return null;
      return carsByInstructorId.get(instructorId) || null;
   }, [carsByInstructorId, instructorId]);

   const currentUser = useMemo(() => {
      const userId = String(currentInstructor?.userId ?? "").trim();
      if (!userId) return null;
      return (
         (users || []).find((row) => String(row?.id ?? "") === userId) || null
      );
   }, [currentInstructor?.userId, users]);

   // fotografia contului instructorului: administratorul și managerul, sau instructorul însuși (serverul verifică la fel)
   const [photoOverride, setPhotoOverride] = useState(undefined);
   const photoUserId = Number(currentInstructor?.userId) || null;
   const viewerRoleUp = String(viewer?.role || "").toUpperCase();
   const photoIsSelf = Boolean(photoUserId) && Number(viewer?.id) === photoUserId;
   const canEditPhoto = Boolean(photoUserId) && (photoIsSelf || viewerRoleUp === "ADMIN" || viewerRoleUp === "MANAGER");
   const photoPerson = useMemo(() => {
      if (!currentInstructor || photoOverride === undefined) return currentInstructor;
      return { ...currentInstructor, avatarUrl: photoOverride };
   }, [currentInstructor, photoOverride]);

   const buildProfileFormData = useCallback(
      () => ({
         firstName: currentInstructor?.firstName || "",
         lastName: currentInstructor?.lastName || "",
         phone: currentInstructor?.phone || "",
         email: currentUser?.email || currentInstructor?.email || "",
         sector: currentInstructor?.sector || "",
         instructorType: normalizeInstructorType(currentInstructor?.instructorType),
         isExamen: normalizeInstructorExam(currentInstructor?.isExamen),
         instructorIdField: String(currentInstructor?.id || ""),
         color: String(currentInstructor?.color || ""),
         carPlate: currentCar?.plateNumber || "",
         gearbox: toApiGearbox(currentCar?.gearbox || "manual"),
         newPassword: "",
      }),
      [currentCar?.gearbox, currentCar?.plateNumber, currentInstructor, currentUser?.email],
   );

   useEffect(() => {
      if (!currentInstructor) return;
      if (profileDirty) return;
      setFormData(buildProfileFormData());
   }, [buildProfileFormData, currentInstructor, profileDirty]);

   useEffect(() => {
      setTab("profile");
      setProfileDirty(false);
      setSaveError("");
      setRatingsAnchor(getCurrentMonthSlot());
      setRatingsFullYear(false);
      ratingsCacheRef.current = { instructorId: "", rows: [] };
      setRatingsAll([]);
      setRatingsError("");
   }, [instructorId]);

   useEffect(() => {
      if (!instructorId) return undefined;
      if (tab !== "stats") return undefined;

      const cached = ratingsCacheRef.current;
      if (cached.instructorId === instructorId && cached.rows.length) {
         setRatingsAll(cached.rows);
         setRatingsLoading(false);
         setRatingsError("");
         return undefined;
      }

      let canceled = false;

      (async () => {
         setRatingsLoading(true);
         setRatingsError("");
         try {
            const allRows = await getAdminRatingsAll({ instructorId });
            if (canceled) return;

            const rows = Array.isArray(allRows) ? allRows : [];
            ratingsCacheRef.current = { instructorId, rows };
            setRatingsAll(rows);
         } catch (error) {
            if (!canceled) {
               setRatingsAll([]);
               setRatingsError(
                  error?.message || "Nu am putut încărca statisticile.",
               );
            }
         } finally {
            if (!canceled) setRatingsLoading(false);
         }
      })();

      return () => {
         canceled = true;
      };
   }, [instructorId, tab]);

   const ratingsYear = ratingsAnchor.year;
   const ratingsMonth = ratingsAnchor.month;

   const visibleMonthWindow = useMemo(
      () => buildVisibleMonthWindow(ratingsAnchor),
      [ratingsAnchor],
   );

   const canShiftMonthForward =
      compareMonthSlots(ratingsAnchor, getCurrentMonthSlot()) < 0;

   const shiftRatingsMonth = useCallback((delta) => {
      setRatingsFullYear(false);
      setRatingsAnchor((current) => {
         const next = shiftMonthSlot(current, delta);
         const now = getCurrentMonthSlot();
         if (compareMonthSlots(next, now) > 0) return current;
         return next;
      });
   }, []);

   const selectRatingsMonth = useCallback((slot) => {
      setRatingsFullYear(false);
      setRatingsAnchor(slot);
   }, []);

   const selectedPeriodStats = useMemo(
      () =>
         computeRatingStats(
            filterRatingsByPeriod(ratingsAll, {
               year: ratingsYear,
               month: ratingsMonth,
               fullYear: ratingsFullYear,
            }),
         ),
      [ratingsAll, ratingsFullYear, ratingsMonth, ratingsYear],
   );

   const periodFilteredRatings = useMemo(
      () =>
         [...filterRatingsByPeriod(ratingsAll, {
            year: ratingsYear,
            month: ratingsMonth,
            fullYear: ratingsFullYear,
         })].sort((left, right) => {
            const leftTime = getRatingTimestamp(left)?.getTime() || 0;
            const rightTime = getRatingTimestamp(right)?.getTime() || 0;
            return rightTime - leftTime;
         }),
      [ratingsAll, ratingsFullYear, ratingsMonth, ratingsYear],
   );

   const ratingsTrendBuckets = useMemo(
      () => buildTrendBuckets(ratingsAll, ratingsAnchor),
      [ratingsAll, ratingsAnchor],
   );

   const selectedPeriodCardStyle = useMemo(
      () => getRatingCardStyle(selectedPeriodStats.average),
      [selectedPeriodStats.average],
   );

   const minCardStyle = useMemo(
      () => getRatingCardStyleSoft(selectedPeriodStats.min),
      [selectedPeriodStats.min],
   );

   const maxCardStyle = useMemo(
      () => getRatingCardStyleSoft(selectedPeriodStats.max),
      [selectedPeriodStats.max],
   );

   const currentMonthSlot = useMemo(() => getCurrentMonthSlot(), []);

   const selectedPeriodLabel = useMemo(
      () =>
         formatStatsPeriodLabel({
            year: ratingsYear,
            month: ratingsMonth,
            fullYear: ratingsFullYear,
         }),
      [ratingsFullYear, ratingsMonth, ratingsYear],
   );

   const statsTrendPeriodKey = useMemo(
      () =>
         ratingsFullYear
            ? `year-${ratingsYear}`
            : `month-${ratingsYear}-${ratingsMonth}`,
      [ratingsFullYear, ratingsMonth, ratingsYear],
   );

   const handleFormChange = (event) => {
      const { name, value } = event.target;
      setProfileDirty(true);
      setFormData((current) => ({
         ...current,
         [name]: value,
      }));
   };

   const setColorFromIndex = useCallback((nextIndex) => {
      setProfileDirty(true);
      setFormData((current) => ({
         ...current,
         color: colorIndexToCss(nextIndex),
      }));
   }, []);

   const handleResetProfile = () => {
      if (!currentInstructor) return;
      setSaveError("");
      setProfileDirty(false);
      setFormData(buildProfileFormData());
   };

   const upsertCarForInstructor = async ({ id, plate, gearbox }) => {
      const normalizedPlate = normPlate(plate);
      const existing = carsByInstructorId.get(String(id));

      if (!normalizedPlate) {
         if (existing) {
            await dispatch(removeCar(existing.id)).unwrap();
         }
         return;
      }

      const duplicate = carsByPlate.get(normalizedPlate);

      if (duplicate && String(duplicate?.instructorId) !== String(id)) {
         // mașina trece la acest instructor; mașina lui de până acum o șterge serverul
         await dispatch(
            updateCar({
               id: duplicate.id,
               plateNumber: duplicate.plateNumber,
               instructorId: id,
               gearbox: toApiGearbox(gearbox),
            }),
         ).unwrap();

         return;
      }

      const payload = {
         plateNumber: String(plate || "").trim(),
         instructorId: id,
         gearbox: toApiGearbox(gearbox),
      };

      if (existing) {
         await dispatch(updateCar({ id: existing.id, ...payload })).unwrap();
      } else {
         await dispatch(addCar(payload)).unwrap();
      }
   };

   const handleSaveEdit = async () => {
      if (!currentInstructor?.id) return;

      const firstName = String(formData.firstName || "").trim();
      const lastName = String(formData.lastName || "").trim();
      const phone = String(formData.phone || "").trim();
      const email = String(formData.email || "").trim();
      const sector = String(formData.sector || "").trim();
      const selectedType = normalizeInstructorType(formData.instructorType);
      const isExamen = formData.isExamen === true || selectedType === "EXAMEN";
      const instructorType = isExamen ? "EXAMEN" : selectedType;

      if (!firstName || !lastName) {
         setSaveError("Completează prenumele și numele.");
         return;
      }

      if (!sector) {
         setSaveError("Completează sectorul.");
         return;
      }

      // parolă nouă = contul trece la alt om: cel vechi nu mai poate intra
      const newPassword = String(formData.newPassword || "");
      if (newPassword && newPassword.length < 6) {
         setSaveError("Parola nouă trebuie să aibă cel puțin 6 caractere.");
         return;
      }
      if (
         newPassword &&
         typeof window !== "undefined" &&
         !window.confirm(
            [
               "Contul instructorului primește o parolă nouă.",
               "Parola veche nu mai merge, iar contul se deconectează de pe toate dispozitivele.",
               "Continui?",
            ].join("\n"),
         )
      ) {
         return;
      }

      setSaving(true);
      setSaveError("");

      try {
         if (currentInstructor?.userId) {
            await apiUpdateUser(currentInstructor.userId, { email });
         }

         await dispatch(
            updateInstructor({
               id: currentInstructor.id,
               data: {
                  firstName,
                  lastName,
                  phone,
                  email,
                  sector,
                  instructorType,
                  isExamen,
                  id: String(formData.instructorIdField || "").trim() || undefined,
                  color: String(formData.color || "").trim(),
                  ...(newPassword ? { newPassword } : {}),
               },
            }),
         ).unwrap();

         await upsertCarForInstructor({
            id: currentInstructor.id,
            plate: formData.carPlate,
            gearbox: formData.gearbox,
         });

         await Promise.all([
            dispatch(fetchInstructors()),
            dispatch(fetchCars()),
            dispatch(fetchUsers()),
         ]);
         setProfileDirty(false);
      } catch (error) {
         setSaveError(error?.message || "Nu am putut salva modificările.");
      } finally {
         setSaving(false);
      }
   };

   // Ștergerea instructorului (admin/manager) = ștergerea logică a contului
   // lui: dispare din liste, nu se mai poate conecta, istoricul rămâne.
   // Reîmprospătăm listele ca pagina „Personal" să rămână corectă.
   const viewerRole = String(viewer?.role || "")
      .trim()
      .toUpperCase();
   const isViewerSelf =
      Boolean(viewer?.id) &&
      Boolean(currentInstructor?.userId) &&
      String(viewer.id) === String(currentInstructor.userId);
   const canDeleteInstructor =
      Boolean(currentInstructor?.id) &&
      (viewerRole === "ADMIN" || viewerRole === "MANAGER") &&
      !isViewerSelf;
   // parola nouă (contul trece la alt om) o dau doar adminul și managerul
   const canSetInstructorPassword =
      Boolean(currentInstructor?.userId) &&
      (viewerRole === "ADMIN" || viewerRole === "MANAGER");

   const handleDeleteInstructor = async () => {
      if (!currentInstructor?.id || deleting || saving) return;

      setDeleting(true);
      setSaveError("");

      try {
         await dispatch(
            removeInstructorAccount({
               instructorId: currentInstructor.id,
               userId: currentInstructor.userId ?? null,
            }),
         ).unwrap();
         dispatch(fetchInstructors());
         dispatch(fetchUsers());
         dispatch(fetchCars());
         closePopupUI();
      } catch (error) {
         setSaveError(error?.message || "Nu am putut șterge instructorul.");
      } finally {
         setDeleting(false);
      }
   };

   if (!currentInstructor) {
      return (
         <div className="instructorProfileUI">
            <div className="instructorProfileUI__header popupPanelUI__header">
               <h3 className=" instructorProfileUI__title">
                  Instructor
               </h3>
            </div>
            <div className="instructorProfileUI__content">
               <p className="instructorProfileUI__loading">Se încarcă...</p>
            </div>
         </div>
      );
   }

   const fullName =
      `${currentInstructor?.firstName || ""} ${
         currentInstructor?.lastName || ""
      }`.trim() || "Instructor";
   const sector = currentInstructor?.sector || "—";
   const instructorType = normalizeInstructorType(
      currentInstructor?.instructorType,
   );
   const rawColor = String(currentInstructor?.color || "").trim();
   const avatarColor = normalizeColorToCss(
      rawColor,
      getAvatarColorFromName(currentInstructor),
   );
   const activeColorIndex = colorIndexFromColor(
      formData.color || rawColor,
      DEFAULT_COLOR_INDEX,
   );
   // sub nume: titlul și, dacă are mașină, sectorul („Instructor de bază · Ciocana”)
   const headSubtitle = [
      getStaffTitle("INSTRUCTOR", instructorType),
      instructorTypeHasCar(instructorType) && sector !== "—" ? sector : null,
   ]
      .filter(Boolean)
      .join(" · ");

   return (
      <div className="instructorProfileUI staffProfilePopup">
         <div className="instructorProfileUI__header popupPanelUI__header staffProfilePopup__header">
            <h3 className=" instructorProfileUI__title">
               Instructor
            </h3>
         </div>

         <div className="instructorProfileUI__content staffProfilePopup__body">
            {/* antetul ca la elev: avatarul (apăsat, alege fotografia — se vede pe loc), numele, titlul */}
            <StaffProfileHead
               person={photoPerson}
               color={avatarColor}
               initials={getInitials(currentInstructor)}
               name={fullName}
               subtitle={headSubtitle}
               photo={
                  canEditPhoto
                     ? {
                          mode: "upload",
                          target: photoIsSelf ? "me" : photoUserId,
                          hasPhoto: Boolean(ownAvatarUrl(photoPerson)),
                          onChanged: (url) => {
                             rememberAvatarChange(photoUserId, url);
                             setPhotoOverride(url);
                          },
                       }
                     : null
               }
            />

            <ProfileTabs
               items={TABS}
               value={tab}
               onChange={(key) => {
                  setSaveError("");
                  setTab(key);
               }}
            />



            {tab === "profile" && (
               <InstructorMainTab
                  formData={formData}
                  handleFormChange={handleFormChange}
                  handleSaveEdit={handleSaveEdit}
                  saveError={saveError}
                  saving={saving}
                  deleting={deleting}
                  canDelete={canDeleteInstructor}
                  canSetPassword={canSetInstructorPassword}
                  handleDeleteInstructor={handleDeleteInstructor}
                  handleResetProfile={handleResetProfile}
                  activeColorIndex={activeColorIndex}
                  setColorFromIndex={setColorFromIndex}
                  setProfileDirty={setProfileDirty}
                  setFormData={setFormData}
               />
            )}

            {tab === "lessons" && (
               <InstructorLessonsTab instructor={currentInstructor} />
            )}

            {tab === "stats" && (
               <InstructorStatsTab
                  shiftRatingsMonth={shiftRatingsMonth}
                  ratingsLoading={ratingsLoading}
                  visibleMonthWindow={visibleMonthWindow}
                  ratingsFullYear={ratingsFullYear}
                  ratingsAnchor={ratingsAnchor}
                  currentMonthSlot={currentMonthSlot}
                  selectRatingsMonth={selectRatingsMonth}
                  canShiftMonthForward={canShiftMonthForward}
                  ratingsYear={ratingsYear}
                  setRatingsFullYear={setRatingsFullYear}
                  ratingsAll={ratingsAll}
                  ratingsError={ratingsError}
                  selectedPeriodCardStyle={selectedPeriodCardStyle}
                  selectedPeriodLabel={selectedPeriodLabel}
                  selectedPeriodStats={selectedPeriodStats}
                  minCardStyle={minCardStyle}
                  maxCardStyle={maxCardStyle}
                  ratingsTrendBuckets={ratingsTrendBuckets}
                  statsTrendPeriodKey={statsTrendPeriodKey}
                  periodFilteredRatings={periodFilteredRatings}
               />
            )}


         </div>
      </div>
   );
}
